/**
 * openEntity — KERNEL "bấm vào 1 thứ trong chat thì mở Ở ĐÂU".
 *
 * Vấn đề gốc (sếp Phú 11.08.2026): "1 số card bấm vào bị load trang trắng rồi mới mở popup".
 * Nguyên nhân: mỗi chỗ tự quyết định cách mở, 4 kiểu khác nhau cùng tồn tại:
 *   1. `window.open(url,'_blank')` khi detailUrl là URL TUYỆT ĐỐI của CHÍNH mình
 *      (BotCard.renderGenericNotification) → tab mới boot lại cả SPA = TRẮNG TRANG,
 *      xong mới đọc `?taskId=` để bung popup.
 *   2. `window.location.href = conv_url` (card escalate hộp thư) → reload cứng = trắng trang.
 *   3. `navigate('/tasks/<id>')` → vào TRANG chi tiết (chunk lazy, fallback `<div/>` = trắng)
 *      trong khi entity đó ĐÃ CÓ popup tại chỗ (RoomEntitiesView, các type LinkUnfurl chưa map).
 *   4. `navigate('https://hub.cnvwork.com/...')` → react-router hiểu là path tương đối → URL rác.
 *
 * Luật CHUẨN (1 nguồn duy nhất, cả Core + SaaS dùng chung file này):
 *   - Có popup tại chỗ  → mở popup, KHÔNG rời trang, KHÔNG reload.
 *   - Nội bộ, không popup → SPA navigate (KHÔNG `window.location`, KHÔNG tab mới).
 *   - Ngoài hệ thống    → tab mới.
 *
 * Popup đăng ký ĐỘNG: mỗi DrawerProvider tự `registerEntityOpener(kind, open)` lúc mount.
 * Nhờ vậy kernel là hàm THUẦN (không hook) — gọi được từ mọi nơi (card, bảng, toast, menu),
 * và host nào không mount provider thì tự động rơi về navigate, không vỡ.
 */

// Map dùng chung kể cả khi module bị bundle 2 lần (xem [[reference_vite_dedup_dev_masks_prod]]).
const REG = (() => {
  const g = typeof globalThis !== 'undefined' ? globalThis : {};
  if (!g.__cnvEntityOpeners) g.__cnvEntityOpeners = new Map();
  return g.__cnvEntityOpeners;
})();

/**
 * Đăng ký "loại bản ghi này mở bằng popup nào". Gọi trong useEffect của Provider.
 * @returns hàm huỷ đăng ký (trả về từ useEffect).
 */
export function registerEntityOpener(kind, open) {
  if (!kind || typeof open !== 'function') return () => {};
  REG.set(kind, open);
  return () => { if (REG.get(kind) === open) REG.delete(kind); };
}

/**
 * Hàm mở popup đã đăng ký cho loại này, hoặc null nếu host không mount Provider tương ứng.
 *
 * Đây là đường VÒNG QUA REACT CONTEXT: sổ đăng ký nằm ở module scope nên với tới được
 * `openXxx` thật kể cả khi nơi gọi không phải con cháu của Provider trên cây React
 * (xem chú thích NO_PROVIDER trong FulfillmentOrderDrawerContext).
 */
export function getEntityOpener(kind) {
  return (kind && REG.get(kind)) || null;
}

export function hasEntityOpener(kind) {
  return !!getEntityOpener(kind);
}

/** ID hợp lệ = short id 8 hex (utils/shortId) hoặc UUID. Chặn nuốt sub-route chữ
 *  (`/tasks/my-tasks`, `/approvals/templates`) làm id → mở popup rỗng. */
const ID_RE = /^[0-9a-f]{8}[0-9a-f-]*$/i;

/**
 * Bảng luật: 1 loại bản ghi = 1 query param (nguồn sự thật của popup) + các path chi tiết.
 * THÊM LOẠI MỚI: thêm 1 dòng ở đây + `registerEntityOpener` trong Provider là xong,
 * mọi surface (chat card, bảng, notify) tự hưởng.
 */
export const ENTITY_LINK_RULES = [
  { kind: 'task',         param: 'taskId',         paths: [/^\/tasks\/([\w-]+)/] },
  { kind: 'approval',     param: 'approvalId',     paths: [/^\/approvals?\/([\w-]+)/], queryOn: [/^\/approvals?(?:$|\?)/], queryKey: 'id' },
  { kind: 'sharedList',   param: 'sharedListId',   paths: [], queryOn: [/^\/approvals?\/shared/], queryKey: 'list_id' },
  { kind: 'contact',      param: 'contactId',      paths: [/^\/crm\/contacts\/([\w-]+)/] },
  { kind: 'company',      param: 'companyId',      paths: [/^\/crm\/companies\/([\w-]+)/] },
  { kind: 'deal',         param: 'dealId',         paths: [/^\/crm\/deals\/([\w-]+)/] },
  { kind: 'lead',         param: 'leadId',         paths: [/^\/crm\/leads\/([\w-]+)/] },
  { kind: 'account',      param: 'accountId',      paths: [/^\/(?:crm|fulfillment)\/accounts\/([\w-]+)/] },
  { kind: 'invoice',      param: 'invoiceId',      paths: [/^\/crm\/invoices\/([\w-]+)/] },
  { kind: 'quotation',    param: 'quotationId',    paths: [/^\/crm\/quotations\/([\w-]+)/] },
  { kind: 'subscription', param: 'subscriptionId', paths: [/^\/crm\/subscriptions\/([\w-]+)/], queryOn: [/^\/crm\/subscriptions(?:$|\?)/], queryKey: 'id' },
  { kind: 'ticket',       param: 'ticketId',       paths: [/^\/(?:support\/)?tickets\/([\w-]+)/, /^\/support\/([\w-]+)/] },
  { kind: 'fulfillment',  param: 'orderId',        paths: [/^\/fulfillment\/orders\/([\w-]+)/] },
  // Tin mới sản phẩm (bài soạn ở Core, hiện ở Core + Work). Popup = ProductNewsHost.
  { kind: 'productNews',  param: 'newsId',         paths: [/^\/whats-new\/([\w-]+)/] },
];

const RULE_BY_KIND = new Map(ENTITY_LINK_RULES.map(r => [r.kind, r]));

/** URL/path bất kỳ → path nội bộ ('/a/b?c=d'), hoặc null nếu là link NGOÀI hệ thống.
 *
 *  "Nội bộ" = CÙNG BẢN TRIỂN KHAI đang chạy, không phải "cùng công ty CNV". Trước đây
 *  mọi host chứa 'cnvwork.com' đều tính là nội bộ ⇒ ở Core (self-hosted, tên miền
 *  riêng) mà bấm link `hub.cnvwork.com/...` của SaaS thì Core lại `navigate` path đó
 *  trong chính nó → không có route → catch-all admin đá về /dashboard. Chỉ *.cnvwork.com
 *  nói chuyện với nhau khi CHÍNH MÌNH cũng đang ở *.cnvwork.com. */
const CNVWORK_HOST_RE = /(^|\.)cnvwork\.com$/i;

/* ───────── VÁ path chết trước khi điều hướng (sếp Phú 25.08.2026) ─────────
 * "click vào link trong tin nhắn là ra trang tổng quan" — lỗi lặp lại NHIỀU LẦN vì
 * mỗi lần chỉ vá đúng 1 ca. Cơ chế chung: host nào cũng có catch-all `path="*"` →
 * /dashboard, nên MỌI path không khớp route đều âm thầm thành "Tổng quan", không
 * một dấu hiệu lỗi. Bảng dưới vá 2 lớp:
 *   1. Link BỊ HỎNG KHI DÁN — dán 2 lần dính liền `/crm/x/idhttps://host/crm/x/id`.
 *   2. Link ĐÚNG nhưng trang chi tiết KHÔNG có route riêng (chi tiết là popup trên
 *      trang danh sách). Bot bắn đúng URL đó để unfurl ra card (URL chính là NGUỒN
 *      DỮ LIỆU của card) ⇒ KHÔNG được sửa ở nơi gửi, phải quy đổi lúc MỞ.
 * Host cũng phải có route redirect tương ứng: link mở bằng tab mới (ChatLink dùng
 * `<a target="_blank">`) nạp thẳng URL, không đi qua kernel này.
 */
const PATH_REWRITES = [
  // Thanh toán: chi tiết = popup trên /crm/payments (?payment=). Bot "Tiền về" bắn
  // /crm/payments/<id> mỗi lần có tiền về.
  [/^\/crm\/payments\/([0-9a-f][\w-]{7,})(?:\?(.*))?$/i, (m) => `/crm/payments?payment=${m[1]}`],
  // Gói thuê bao: cũng là popup trên trang danh sách (?id=) — khớp entityFallbackPath.
  [/^\/crm\/subscriptions\/([0-9a-f][\w-]{7,})(?:\?(.*))?$/i, (m) => `/crm/subscriptions?id=${m[1]}`],
  // Chấm công: bot HR bắn /attendance/<id>?ctx=checkin MỖI LẦN check-in/check-out
  // (attendanceController) — không hề có route /attendance/:id ⇒ đây là ca "ra Tổng
  // quan" gặp nhiều nhất, mỗi người 2 lần/ngày. Đích đúng: lịch sử chấm công của mình.
  [/^\/(?:my-)?attendance\/(?!remind\/)([0-9a-f][\w-]{7,})(?:\?(.*))?$/i, () => '/my-attendance'],
  // Ứng viên: không có trang chi tiết riêng, hồ sơ là popup trên trang danh sách
  // (?candidate= — thêm 25.08.2026 cùng lúc với luật này).
  [/^\/recruitment\/candidates\/([0-9a-f][\w-]{7,})(?:\?(.*))?$/i, (m) => `/recruitment/candidates?candidate=${m[1]}`],
  // Phòng chat dạng path (link cũ / app mobile) → tham số phòng của trang /chat.
  [/^\/chat\/([0-9a-f][\w-]{7,})(?:\?(.*))?$/i, (m) => `/chat?group=${m[1]}`],
  // Đơn duyệt viết số ít (vài trang marketing dựng sai) → route thật là số nhiều.
  [/^\/approval\/([0-9a-f][\w-]{7,})(?:\?(.*))?$/i, (m) => `/approvals/${m[1]}`],
];

/** URL dán 2 lần dính liền: lấy URL NẰM TRONG làm đích thật. Đã thấy trên prod:
 *  `/crm/companies/35bcf83ahttps://connect.cnvwork.com/crm/companies/35bcf83a`.
 *
 *  CHỈ bóc khi URL dính nằm ở phần ĐƯỜNG DẪN và trỏ về CHÍNH host này. Không được
 *  đụng tới query: `/inbox?next=https://ngoai.com` là tham số hợp lệ, bóc ra thành
 *  đích điều hướng là mở đường dắt người dùng sang tên miền lạ. */
function unstickEmbeddedUrl(s) {
  const qi = s.indexOf('?');
  const head = qi === -1 ? s : s.slice(0, qi);
  const i = head.search(/https?:\/\//i);
  if (i <= 0) return s;
  const inner = s.slice(i);
  try {
    const host = new URL(inner).hostname;
    const self = typeof window !== 'undefined' ? window.location.hostname : '';
    if (host !== self && !(CNVWORK_HOST_RE.test(host) && CNVWORK_HOST_RE.test(self))) return s;
  } catch { return s; }
  return inner;
}

function applyRewrites(path) {
  for (const [re, to] of PATH_REWRITES) {
    const m = re.exec(path);
    if (m) return to(m);
  }
  return path;
}

export function toInternalPath(raw, _depth = 0) {
  const s = unstickEmbeddedUrl(typeof raw === 'string' ? raw.trim() : '');
  if (!s) return null;
  // '//host/x' là URL protocol-relative (đi ra NGOÀI), không phải path nội bộ — chặn kẻo
  // navigate/location.assign dắt người dùng sang tên miền lạ.
  if (s.startsWith('//')) return null;
  if (s.startsWith('/')) {
    // Path còn dính URL tuyệt đối bên trong (`/crm/x/id` + `https://…`) → bóc ra rồi
    // giải lại từ đầu; _depth chặn vòng lặp nếu chuỗi kỳ quái.
    const inner = unstickEmbeddedUrl(s);
    if (inner !== s && _depth < 2) return toInternalPath(inner, _depth + 1);
    return applyRewrites(s);
  }
  try {
    const u = new URL(s);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
    const self = typeof window !== 'undefined' ? window.location.hostname : '';
    const internal = u.hostname === self
      || (CNVWORK_HOST_RE.test(u.hostname) && CNVWORK_HOST_RE.test(self));
    if (!internal) return null;
    const path = u.pathname + u.search;
    const inner = unstickEmbeddedUrl(path);
    if (inner !== path && _depth < 2) return toInternalPath(inner, _depth + 1);
    return applyRewrites(path);
  } catch { return null; }
}

/**
 * Link này có mở được bằng POPUP TẠI CHỖ không? → `{kind,id}` nếu có, null nếu không.
 *
 * Dùng cho LINK (text trong tin nhắn, chip link trơn): chỉ được chặn click khi chắc
 * chắn có popup để bung. Không có popup thì PHẢI để `<a target="_blank">` mở tab mới
 * tới trang thật — `navigate(path)` mù với path không phải route SPA sẽ bị catch-all
 * của host đá về /dashboard (sếp Phú 03.07.2026 "click link vào nhầm dashboard").
 * CARD thì khác: card biết trước `{kind,id}` nên vẫn đi lối `openEntityLink`.
 */
export function popupTargetFor(raw) {
  const path = toInternalPath(raw);
  if (!path) return null;
  const hit = matchEntityLink(path);
  if (!hit || !hasEntityOpener(hit.kind)) return null;
  return hit;
}

/** path nội bộ → { kind, id } nếu là link tới 1 bản ghi có popup, ngược lại null. */
export function matchEntityLink(path) {
  if (!path || typeof path !== 'string') return null;
  const qIndex = path.indexOf('?');
  const pathname = qIndex === -1 ? path : path.slice(0, qIndex);
  let query;
  try { query = new URLSearchParams(qIndex === -1 ? '' : path.slice(qIndex + 1)); }
  catch { query = new URLSearchParams(); }

  // 1. Query param của chính popup (`?taskId=`, `?dealId=`...) — link copy từ URL khi popup
  //    đang mở, hoặc detail_url mẫu thông báo. Ưu tiên cao nhất: đúng ý người gửi link.
  for (const rule of ENTITY_LINK_RULES) {
    const v = query.get(rule.param);
    if (v) return { kind: rule.kind, id: v };
  }
  // 2. Query "id trần" chỉ nhận trên đúng path đã khai báo (`/approvals?id=`,
  //    `/crm/subscriptions?id=`) — KHÔNG nhận `?id=` chung chung (vd `/inbox?id=` là hội thoại).
  for (const rule of ENTITY_LINK_RULES) {
    if (!rule.queryOn || !rule.queryKey) continue;
    if (!rule.queryOn.some(re => re.test(path))) continue;
    const v = query.get(rule.queryKey);
    if (v) return { kind: rule.kind, id: v };
  }
  // 3. Path chi tiết `/tasks/<id>`, `/crm/deals/<id>`...
  for (const rule of ENTITY_LINK_RULES) {
    for (const re of rule.paths || []) {
      const m = re.exec(pathname);
      if (m && m[1] && ID_RE.test(m[1])) return { kind: rule.kind, id: m[1] };
    }
  }
  return null;
}

/** kind + id → path chi tiết full-page (đường lui khi host không mount popup). */
export function entityFallbackPath(kind, id) {
  if (!RULE_BY_KIND.has(kind) || !id) return null;
  // Dựng lại path từ regex là mong manh → map tay theo kind cho rõ ràng.
  switch (kind) {
    case 'task':         return `/tasks/${id}`;
    case 'approval':     return `/approvals/${id}`;
    case 'contact':      return `/crm/contacts/${id}`;
    case 'company':      return `/crm/companies/${id}`;
    case 'deal':         return `/crm/deals/${id}`;
    case 'lead':         return `/crm/leads/${id}`;
    case 'account':      return `/crm/accounts/${id}`;
    case 'invoice':      return `/crm/invoices/${id}`;
    case 'quotation':    return `/crm/quotations/${id}`;
    case 'subscription': return `/crm/subscriptions?id=${id}`;
    case 'ticket':       return `/tickets/${id}`;
    case 'fulfillment':  return `/fulfillment/orders/${id}`;
    case 'productNews':  return `/whats-new/${id}`;
    default:             return null;
  }
}

/**
 * MỞ 1 thứ được bấm trong chat (card unfurl, card bot, dòng bảng, link).
 *
 * @param {string|{kind?:string,id?:string,url?:string}} target
 *        - string: URL/path bất kỳ.
 *        - object: `{kind,id}` khi nơi gọi ĐÃ biết mình là gì (card unfurl có payload.type),
 *          kèm `url` làm đường lui.
 * @param {{navigate?:Function}} opts  navigate của react-router (bắt buộc để đi trong SPA).
 * @returns {'popup'|'navigate'|'external'|'none'} đã xử lý theo cách nào (để test/log).
 */
export function openEntityLink(target, { navigate } = {}) {
  const t = typeof target === 'string' ? { url: target } : (target || {});

  // 1. Biết trước loại + id → popup ngay, khỏi đoán qua URL.
  if (t.kind && t.id) {
    const open = REG.get(t.kind);
    if (open) { open(t.id); return 'popup'; }
  }

  const raw = t.url || (t.kind && t.id ? entityFallbackPath(t.kind, t.id) : null);
  if (!raw) return 'none';

  const path = toInternalPath(raw);
  // 2. Ngoài hệ thống → tab mới (giữ nguyên hành vi cũ).
  if (!path) {
    if (/^https?:\/\//i.test(raw)) window.open(raw, '_blank', 'noopener');
    return 'external';
  }
  // 3. Nội bộ: có popup thì mở popup (Ở LẠI trang hiện tại — không mất khung chat).
  const hit = matchEntityLink(path);
  if (hit) {
    const open = REG.get(hit.kind);
    if (open) { open(hit.id); return 'popup'; }
  }
  // 4. Nội bộ không popup → SPA navigate. TUYỆT ĐỐI không window.location (reload = trắng trang).
  if (typeof navigate === 'function') {
    try { navigate(path); return 'navigate'; } catch { /* rơi xuống dưới */ }
  }
  window.location.assign(path);
  return 'navigate';
}
